import { Box, Card, CardActionArea, Chip, CircularProgress, FormControl, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { ReactElement, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSelector } from 'react-redux';
import { Navigate, useNavigate } from 'react-router-dom';
import { QuerySiteStatusEntry } from '../../api/schema';
import useAppDispatch from '../../hooks/useAppDispatch';
import { useIsRemediation } from '../../hooks/useIsRemediation';
import useSearchParams from '../../hooks/useSearchParams';
import { getJobSiteStatusBoard, getJobSiteStatusBoardFetching, getJobSiteStatusBoardSelector } from '../../redux/slices/assignments/assignmentsSlice';
import { getMarketsList } from '../../redux/slices/verticalMarkets/verticalMarketsSlice';

const COLUMNS: { key: string; label: string; accent: string }[] = [
  { key: 'prepared', label: 'Prepared', accent: 'grey.500' },
  { key: 'in_progress', label: 'In-Progress', accent: 'primary.main' },
  { key: 'ready_for_pickup', label: 'Ready For Pickup', accent: 'warning.main' },
  { key: 'scheduled_for_pickup', label: 'Scheduled For Pickup', accent: 'info.main' },
  { key: 'cleared', label: 'Cleared', accent: 'success.main' },
];

// Map a backend status entry to the board column(s) it belongs in (a site can
// be in more than one). In-Progress holds every on-site ('delivered') jobsite
// regardless of the Look Back window. Cleared shows only sites cleared within
// the window. In-transit (possessed, en route) folds into Prepared now that its
// own column is gone. `recent` = status_at within window.
const columnKeysFor = (status: string | undefined, recent: boolean): string[] => {
  switch (status) {
    case 'prepared':
    case 'in_transit':
      return ['prepared'];
    case 'ready_for_pickup':
      return ['ready_for_pickup'];
    case 'scheduled_for_pickup':
      return ['scheduled_for_pickup'];
    case 'delivered':
      return ['in_progress'];
    case 'cleared':
      return recent ? ['cleared'] : [];
    default:
      return [];
  }
};

// Backend returns up to 10 days of state history; the Look Back window filters
// entries to those whose last state change falls within the selected span.
const LOOKBACK_MAX_DAYS = 10;

// Rendering every historical site as a card makes the page crawl (the Cleared
// column alone can hold 1000+); the column header still shows the full count.
const MAX_CARDS_PER_COLUMN = 100;

// Sentinel PM-filter value for "no project manager assigned" (a value that can't
// collide with a real PM name).
const NO_PM = '__none__';

// Backend returns the project manager as "First Last"; the board shows it as
// "Last, First". Split on the final space so multi-word first names stay intact.
const formatPM = (name?: string): string => {
  const trimmed = name?.trim();
  if (!trimmed) return '';
  const lastSpace = trimmed.lastIndexOf(' ');
  return lastSpace === -1 ? trimmed : `${trimmed.slice(lastSpace + 1)}, ${trimmed.slice(0, lastSpace)}`;
};

// Card date grid shows the day only (no time); "—" when the milestone hasn't
// happened yet.
const formatDate = (iso?: string): string => (iso ? DateTime.fromISO(iso).toLocaleString(DateTime.DATE_MED) : '—');

// A pickup date that has arrived or passed is overdue (compared by calendar day).
const isPickupOverdue = (iso?: string): boolean => !!iso && DateTime.fromISO(iso).startOf('day') <= DateTime.now().startOf('day');

// Whole days elapsed since an ISO timestamp; null when the timestamp is absent.
const daysSince = (iso?: string): number | null => (iso ? Math.max(0, Math.floor(DateTime.now().diff(DateTime.fromISO(iso), 'days').days)) : null);

// Notes preview: first 40 characters, with an ellipsis when truncated.
const NOTES_PREVIEW_LEN = 40;
const notesPreview = (notes?: string): string => {
  const trimmed = notes?.trim() ?? '';
  return trimmed.length > NOTES_PREVIEW_LEN ? `${trimmed.slice(0, NOTES_PREVIEW_LEN)}...` : trimmed;
};

// Read-only Kanban: every job site with equipment connected to it, in its
// single current lifecycle state, newest state-change on top.
export const JobSiteSummary = (): ReactElement => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  // The pipeline board is a Remediation view: the menu hides the tab for other
  // markets, and a bookmarked link does not get past this.
  const isRemediation = useIsRemediation();
  const markets = useSelector(getMarketsList({}));
  const entries = useSelector(getJobSiteStatusBoardSelector);
  const fetching = useSelector(getJobSiteStatusBoardFetching);
  // Filters live in the URL so the view is shareable/bookmarkable. Absent params
  // fall back to the defaults (all PMs, 5-day look back).
  const [urlParams, setURLParams] = useSearchParams();
  const lookbackDays = urlParams.lookback ? Number(urlParams.lookback) : 5;
  const pmFilter = urlParams.pm ?? 'all';

  useEffect(() => {
    dispatch(getJobSiteStatusBoard());
  }, [dispatch]);

  // Project managers present on the board, for the PM filter dropdown.
  const pmOptions = useMemo(() => {
    const names = new Set<string>();
    (entries ?? []).forEach((entry) => {
      if (entry.project_manager) names.add(entry.project_manager);
    });
    return Array.from(names).sort((a, b) => formatPM(a).localeCompare(formatPM(b)));
  }, [entries]);

  // Entries arrive newest state-change first; bucketing in that order keeps each
  // column sorted newest -> oldest vertically. In-Progress/Cleared are derived
  // from the backend status + whether the change falls in the Look Back window
  // (see columnKeysFor).
  const columns = useMemo(() => {
    const cutoff = DateTime.now().minus({ days: lookbackDays }).startOf('day');
    const buckets: Record<string, QuerySiteStatusEntry[]> = Object.fromEntries(COLUMNS.map((column) => [column.key, []]));
    (entries ?? []).forEach((entry) => {
      if (pmFilter === NO_PM) {
        if (entry.project_manager) return; // keep only sites with no PM
      } else if (pmFilter !== 'all' && entry.project_manager !== pmFilter) {
        return;
      }
      const recent = !!entry.status_at && DateTime.fromISO(entry.status_at) >= cutoff;
      columnKeysFor(entry.status, recent).forEach((key) => buckets[key].push(entry));
    });
    // Scheduled For Pickup lists the earliest pickup date first (most imminent /
    // overdue on top). Every entry here has a pickup_date by definition.
    buckets.scheduled_for_pickup.sort((a, b) => {
      const da = a.pickup_date ? DateTime.fromISO(a.pickup_date).toMillis() : Number.POSITIVE_INFINITY;
      const db = b.pickup_date ? DateTime.fromISO(b.pickup_date).toMillis() : Number.POSITIVE_INFINITY;
      return da - db;
    });
    return COLUMNS.map((column) => ({ ...column, sites: buckets[column.key] }));
  }, [entries, lookbackDays, pmFilter]);

  if (markets.result && !isRemediation) {
    return <Navigate to="/activity/dashboard/summary" replace />;
  }

  return (
    <>
      <Helmet title="Pipeline" />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 2 }}>
        <Box sx={{ maxWidth: 780, border: 2, borderColor: 'grey.700', borderRadius: 1, px: 3, py: 1 }}>
          <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
            Pipeline — every job site with equipment, by stage: Prepared → In-Progress → Ready For Pickup → Scheduled For Pickup → Cleared.
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Each card shows its key dates and equipment counts; an overdue pickup turns red. Filter by project manager and how many days of cleared sites stay
            on the board.
          </Typography>
        </Box>
        <Stack direction="row" spacing={2} sx={{ flexShrink: 0 }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="pm-label">Project Manager</InputLabel>
            <Select labelId="pm-label" label="Project Manager" value={pmFilter} onChange={(e) => setURLParams({ newParams: { pm: String(e.target.value) } })}>
              <MenuItem value="all">All PMs</MenuItem>
              <MenuItem value={NO_PM} sx={{ fontWeight: 700 }}>
                No Project Mgr.
              </MenuItem>
              {pmOptions.map((name) => (
                <MenuItem key={name} value={name}>
                  {formatPM(name)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="lookback-label">Look Back</InputLabel>
            <Select
              labelId="lookback-label"
              label="Look Back"
              value={lookbackDays}
              onChange={(e) => setURLParams({ newParams: { lookback: String(e.target.value) } })}
            >
              {Array.from({ length: LOOKBACK_MAX_DAYS }, (_, i) => i + 1).map((days) => (
                <MenuItem key={days} value={days}>
                  {days} {days === 1 ? 'Day' : 'Days'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Box>
      {fetching && !entries ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ overflow: 'auto', maxHeight: 'calc(100vh - 240px)', pb: 2 }}>
          <Box sx={{ display: 'flex', gap: 4, alignItems: 'flex-start', width: 'max-content' }}>
            {columns.map((column) => (
              <Card key={column.key} sx={{ width: 275, flex: '0 0 275px', borderTop: 3, borderColor: column.accent }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 4, py: 3 }}>
                  <Typography variant="h6">{column.label}</Typography>
                  <Chip size="small" label={column.sites.length} />
                </Stack>
                <Stack spacing={2} sx={{ px: 2, pb: 2 }}>
                  {column.sites.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 2 }}>
                      No job sites
                    </Typography>
                  )}
                  {column.sites.slice(0, MAX_CARDS_PER_COLUMN).map((site: QuerySiteStatusEntry) => {
                    // 2x2 grid, filled row-wise: Prepared (TL), Delivered (TR),
                    // Pickup (BL), Cleared (BR). Pickup turns red once it's due/past,
                    // but not once the site is cleared (an overdue pickup is moot).
                    const dateCells = [
                      { label: 'Prepared', value: formatDate(site.prepared_at) },
                      { label: 'Delivered', value: formatDate(site.delivered_at) },
                      {
                        label: 'Pickup',
                        // Not ready -> blank; ready but unscheduled -> "Ready"; ready + scheduled -> the date.
                        value: site.ready_for_pickup !== true ? '' : site.pickup_date ? formatDate(site.pickup_date) : 'Ready',
                        overdue: column.key !== 'cleared' && isPickupOverdue(site.pickup_date),
                      },
                      { label: 'Cleared', value: formatDate(site.cleared_at) },
                    ];
                    // Product-category breakdown, abbreviated, in the same order as the
                    // job-site details "Jobsite Activity" card (Blower, Dehum, Air Scr, Other).
                    const categoryCells = [
                      { label: 'Blow', count: site.blower_cnt ?? 0 },
                      { label: 'Dehum', count: site.dehumidifier_cnt ?? 0 },
                      { label: 'Air Scr', count: site.air_scrubber_cnt ?? 0 },
                      { label: 'Other', count: site.other_cnt ?? 0 },
                    ];
                    // How long the job has been open, measured from delivery.
                    const openDays = daysSince(site.delivered_at);
                    const totalCount = site.item_cnt ?? 0;
                    return (
                      <Card
                        key={site.site_id}
                        variant="outlined"
                        sx={{
                          minHeight: 149,
                          flexShrink: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          bgcolor: 'grey.100',
                          border: 1,
                          borderColor: 'common.black',
                        }}
                      >
                        <CardActionArea
                          onClick={() => navigate(`/customers/job-sites/${site.site_id}/details`)}
                          sx={{ px: 2, pt: 1.25, pb: 2, flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                        >
                          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1} sx={{ pt: 0.5, pb: 1.5 }}>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography variant="subtitle2" noWrap>
                                {site.site_name}
                              </Typography>
                              {site.job_number && (
                                <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                                  Job # {site.job_number}
                                </Typography>
                              )}
                              <Typography variant="body2" color="text.secondary" noWrap>
                                {[site.address, site.city, site.state].filter(Boolean).join(', ')}
                              </Typography>
                              {site.project_manager && (
                                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                                  PM - {formatPM(site.project_manager)}
                                </Typography>
                              )}
                              {site.notes && (
                                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', fontStyle: 'italic' }}>
                                  {notesPreview(site.notes)}
                                </Typography>
                              )}
                            </Box>
                            {column.key !== 'cleared' && openDays !== null && (
                              <Typography variant="caption" noWrap sx={{ flexShrink: 0, fontWeight: 700 }}>
                                Open {openDays} day{openDays === 1 ? '' : 's'}
                              </Typography>
                            )}
                          </Stack>
                          <Stack direction="row" justifyContent="space-between" alignItems="flex-end" spacing={1} sx={{ mt: 'auto', pt: 0.5 }}>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 2, rowGap: 0.5, flex: 1, minWidth: 0 }}>
                              {dateCells.map((cell) => (
                                <Box key={cell.label}>
                                  <Typography
                                    variant="caption"
                                    color={cell.overdue ? 'error.main' : 'text.secondary'}
                                    noWrap
                                    sx={{ display: 'block', fontSize: 10, lineHeight: 1.2, textTransform: 'uppercase', letterSpacing: 0.3 }}
                                  >
                                    {cell.label}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color={cell.overdue ? 'error.main' : 'text.primary'}
                                    noWrap
                                    sx={{ display: 'block', lineHeight: 1.3, fontWeight: cell.overdue ? 700 : 500 }}
                                  >
                                    {cell.value}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                            {column.key !== 'cleared' && (
                              <Box sx={{ flexShrink: 0, minWidth: 66, border: 1, borderColor: 'grey.500', borderRadius: 1, px: 1, py: 0.25 }}>
                                {categoryCells.map((cat) => (
                                  <Stack key={cat.label} direction="row" justifyContent="space-between" spacing={1} sx={{ lineHeight: 1.25 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                                      {cat.label}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 700 }}>
                                      {cat.count}
                                    </Typography>
                                  </Stack>
                                ))}
                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                  spacing={1}
                                  sx={{ lineHeight: 1.25, mt: 0.25, pt: 0.25, borderTop: 1, borderColor: 'grey.400' }}
                                >
                                  <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 700 }}>
                                    Total
                                  </Typography>
                                  <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 700 }}>
                                    {totalCount}
                                  </Typography>
                                </Stack>
                              </Box>
                            )}
                          </Stack>
                        </CardActionArea>
                      </Card>
                    );
                  })}
                  {column.sites.length > MAX_CARDS_PER_COLUMN && (
                    <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 2 }}>
                      …and {column.sites.length - MAX_CARDS_PER_COLUMN} more
                    </Typography>
                  )}
                </Stack>
              </Card>
            ))}
          </Box>
        </Box>
      )}
    </>
  );
};

export default JobSiteSummary;
